import React, { useState, useRef, useEffect } from 'react'
import { useInViewport } from 'ahooks'
import Icon from './Icon'
import { formatRelativeTime } from './utils'
import ImageViewer from './imageViewer'
import PreviewImg from './previewImg'
import { Tooltip } from './tooltip'

export default function HistoryPanel({ open, onClose, items, loading, hasMore, onLoadMore, onDownload, onRegenerate, onViewGenerating }) {
  const [historyDetail, setHistoryDetail] = useState(null)
  const [previewSrc, setPreviewSrc] = useState(null)
  const [, setTick] = useState(0)
  const sentinelRef = useRef(null)
  const loadingRef = useRef(loading)
  loadingRef.current = loading

  useEffect(() => {
    if (!open) {
      setHistoryDetail(null)
      return
    }
    const timer = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(timer)
  }, [open])

  useInViewport(sentinelRef, {
    callback: entry => {
      if (entry.isIntersecting && hasMore && !loadingRef.current) onLoadMore()
    },
  })

  return (
    <>
      <div className={`fdai-history-panel ${open ? 'open' : ''}`}>
        <div className="fdai-history-inner">
          <div className="fdai-history-views">
            {/* 列表视图 */}
            <div className={`fdai-history-view ${historyDetail ? 'slide-out' : 'slide-in'}`}>
              <div className="fdai-history-header">
                <div className="fdai-history-title">
                  <span>历史记录</span>
                  <span className="fdai-history-title-count">共 {items.length} 条记录</span>
                </div>
                <button className="fdai-history-close" onClick={onClose}>
                  <Icon type="zykj-guanbi" />
                </button>
              </div>
              <div className="fdai-history-list">
                {loading && items.length === 0 && <div className="fdai-history-loading">加载中...</div>}
                {!loading && items.length === 0 && <div className="fdai-history-loading">暂无记录</div>}
                {items.map((item, i) => (
                  <div
                    className="fdai-history-item"
                    key={item.id ?? i}
                    onClick={() => {
                      if (item.status === 'generating' && onViewGenerating) {
                        onViewGenerating(item)
                        onClose()
                      } else {
                        setHistoryDetail(item)
                      }
                    }}
                  >
                    <div className="fdai-history-item-thumb">
                      <img src={item.refProduct} alt="" />
                      <div className="fdai-history-item-thumb-tag">1张</div>
                    </div>
                    <div className="fdai-history-item-info">
                      <div className="fdai-history-item-desc">
                        <span className="fdai-history-item-type">
                          {item.tabLabel}
                        </span>
                        <Tooltip zIndex={9999} title={item.desc} placement="top">
                          <span>
                            {item.desc}
                          </span>
                        </Tooltip>
                      </div>
                      <div className="fdai-history-item-ref">
                        {item.refImgs.map((url, i) => (
                          <div key={`${url}-${i}`} className="fdai-history-item-ref-img">
                            <PreviewImg side="bottom" overlayClassName="fdai-scroll-preview-tooltip">
                              <img src={url} alt="" />
                            </PreviewImg>
                          </div>
                        ))}
                      </div>
                      <div className="fdai-history-item-meta">
                        <span className={`fdai-history-item-status ${item.status}`}>
                          <Icon type={item.status === 'success' ? 'zykj-gouxuan' : item.status === 'generating' ? 'icon-jiazai' : 'zykj-guanbi_xiao'} />
                          {item.status === 'success' ? '生成成功' : item.status === 'generating' ? '生成中...' : '生成失败'}
                        </span>
                        <span className="fdai-history-item-time">{formatRelativeTime(item.time)}</span>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={sentinelRef} className="fdai-history-sentinel">
                  {loading && items.length > 0 && <span>加载中...</span>}
                </div>
              </div>
            </div>

            {/* 详情视图 */}
            <div className={`fdai-history-view fdai-history-detail-view ${historyDetail ? 'slide-in' : 'slide-out'}`}>
              <div className="fdai-history-header">
                <div className="fdai-history-title button" onClick={() => setHistoryDetail(null)}>
                  <Icon type="zykj-xiangzuo" />
                  <span className="fdai-history-title">生成详情</span>
                </div>
                <button className="fdai-history-close" onClick={onClose}>
                  <Icon type="zykj-guanbi" />
                </button>
              </div>
              {historyDetail && (
                <div className="fdai-history-detail-body">
                  <div className={`fdai-detail-status ${historyDetail.status}`}>
                    {historyDetail.status === 'success' ? (
                      <>
                        <div className="fdai-detail-status-icon-bg"><Icon type="zykj-gouxuan" /></div>
                        <span>生成成功</span>
                      </>
                    ) : historyDetail.status === 'generating' ? (
                      <>
                        <div className="fdai-detail-status-icon-bg"><Icon type="icon-jiazai" /></div>
                        <span>生成中</span>
                      </>
                    ) : (
                      <>
                        <div className="fdai-detail-status-icon-bg"><Icon type="zykj-guanbi" /></div>
                        <span>生成失败</span>
                      </>
                    )}
                  </div>
                  <div className="fdai-detail-desc">
                    <div className="fdai-detail-desc-label">改款描述</div>
                    <div className="fdai-detail-desc-value">{historyDetail.desc}</div>
                  </div>
                  <div className="fdai-detail-desc">
                    <div className="fdai-detail-desc-label">参考商品</div>
                    <div className="fdai-detail-desc-value">
                      <div className="fdai-detail-desc-img">
                        <PreviewImg side="bottom" overlayClassName="fdai-scroll-preview-tooltip">
                          <img src={historyDetail.refProduct} alt="" />
                        </PreviewImg>
                      </div>
                    </div>
                  </div>
                  <div className="fdai-detail-desc">
                    <div className="fdai-detail-desc-label">参考图片</div>
                    <div className="fdai-detail-desc-value">
                      {historyDetail.refImgs.map((url, i) => (
                        <div key={`${url}-${i}`} className="fdai-detail-desc-img">
                          <PreviewImg side="bottom" overlayClassName="fdai-scroll-preview-tooltip">
                            <img src={url} alt="" />
                          </PreviewImg>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="fdai-detail-desc">
                    <div className="fdai-detail-desc-label">改款方式</div>
                    <div className="fdai-detail-desc-value">{historyDetail.tabLabel}</div>
                  </div>
                  <div className="fdai-detail-desc">
                    <div className="fdai-detail-desc-label">生成结果</div>
                    <div className="fdai-detail-desc-value time">{formatRelativeTime(historyDetail.time)}</div>
                  </div>
                  <div className="fdai-detail-results">
                    {historyDetail.status === 'success' ? (
                      <div className="fdai-detail-result-item success">
                        <img
                          src={historyDetail.results}
                          alt="result"
                          style={{ cursor: 'zoom-in' }}
                          onClick={() => setPreviewSrc(historyDetail.results)}
                        />
                        <div className="fdai-detail-result-item-buttons">
                          <button className="fdai-detail-result-btn" onClick={() => { setHistoryDetail(null); onRegenerate(historyDetail); onClose() }}>
                            <Icon type="icon-zhongxinshengcheng" />
                            <span className="fdai-detail-result-btn-text">重新生成</span>
                          </button>
                          <button className="fdai-detail-result-btn" onClick={() => onDownload(historyDetail.results, historyDetail.workId)}>
                            <Icon type="icon-xiazai1" />
                          </button>
                        </div>
                      </div>
                    ) : historyDetail.status === 'generating' ? (
                      <div className="fdai-detail-result-item generating">
                        <div className="fdai-generate-gradient-placeholder" />
                        <div className="fdai-generate-progress-tag">
                          <span>作品生成中</span>
                        </div>
                      </div>
                    ) : (
                      <div className="fdai-detail-result-item fail">
                        <Icon type="icon-tupian1" />
                        <div className="fdai-detail-result-item-buttons">
                          <button className="fdai-detail-result-btn" onClick={() => { setHistoryDetail(null); onRegenerate(historyDetail); onClose() }}>
                            <Icon type="icon-zhongxinshengcheng" />
                            <span className="fdai-detail-result-btn-text">重新生成</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ImageViewer
        src={previewSrc}
        onClose={() => setPreviewSrc(null)}
        onDownload={onDownload}
        workId={historyDetail?.workId}
      />
    </>
  )
}
